/**
 * Figma-matched Overview widgets ("03 — Dashboard"). Presentational only —
 * every value is caller-supplied from real data; missing values render an
 * honest "—"/empty state. No metric, count, or pill is hard-coded here.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

export type Tone = "brand" | "success" | "warning" | "error" | "info" | "neutral";

// ── Status pill ───────────────────────────────────────────────────────────
export function Pill({
  tone = "neutral",
  label,
  dot = true,
}: {
  tone?: Tone;
  label: string;
  dot?: boolean;
}): JSX.Element {
  return (
    <span className={`ax-pill ax-pill--${tone === "brand" ? "info" : tone}`}>
      {dot ? <span className="ax-pill__dot" aria-hidden="true" /> : null}
      {label}
    </span>
  );
}

// ── Metric card ───────────────────────────────────────────────────────────
export function MetricCard({
  label,
  icon,
  iconTone = "brand",
  value,
  foot,
  pill,
}: {
  label: string;
  icon: ReactNode;
  iconTone?: "brand" | "success" | "warning" | "error";
  /** Real value, or null/undefined → honest "—". */
  value?: string | number | null;
  foot?: ReactNode;
  pill?: { tone: Tone; label: string } | null;
}): JSX.Element {
  const empty = value === null || value === undefined || value === "";
  const iconMod = iconTone === "brand" ? "" : ` ax-metric__icon--${iconTone}`;
  return (
    <div className="ax-metric">
      <div className="ax-metric__top">
        <span className={`ax-metric__icon${iconMod}`}>{icon}</span>
        <span className="ax-metric__label">{label}</span>
        {pill && !empty ? (
          <span className="ax-metric__pill">
            <Pill tone={pill.tone} label={pill.label} />
          </span>
        ) : null}
      </div>
      <div className="ax-metric__body">
        <span className={empty ? "ax-metric__value ax-metric__value--empty" : "ax-metric__value"}>
          {empty ? "—" : value}
        </span>
      </div>
      {foot ? <p className="ax-metric__foot">{foot}</p> : null}
    </div>
  );
}

// ── Standard card / panel ─────────────────────────────────────────────────
export function AdminCard({
  title,
  link,
  children,
}: {
  title: string;
  link?: { label: string; to: string };
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="ax-card">
      <div className="ax-card__head">
        <h2 className="ax-card__title">{title}</h2>
        {link ? (
          <Link className="ax-card__link" to={link.to}>
            {link.label}
          </Link>
        ) : null}
      </div>
      <div className="ax-card__body">{children}</div>
    </section>
  );
}

// ── List row (integration / activity / recommendation) ────────────────────
export function Row({
  mark,
  dot,
  name,
  meta,
  right,
}: {
  mark?: ReactNode;
  dot?: boolean;
  name: ReactNode;
  meta?: ReactNode;
  right?: ReactNode;
}): JSX.Element {
  return (
    <div className="ax-row">
      {mark ? <span className="ax-row__mark">{mark}</span> : null}
      {dot ? <span className="ax-row__dot" aria-hidden="true" /> : null}
      <span className="ax-row__name">{name}</span>
      {meta ? <span className="ax-row__meta">{meta}</span> : null}
      {right}
    </div>
  );
}

// ── Quick action tile ─────────────────────────────────────────────────────
export function QuickActionTile({
  icon,
  label,
  to,
}: {
  icon: ReactNode;
  label: string;
  to: string;
}): JSX.Element {
  return (
    <Link className="ax-qa__tile" to={to}>
      {icon}
      <span className="ax-qa__label">{label}</span>
    </Link>
  );
}

// ── Right-rail card ───────────────────────────────────────────────────────
export function RailCard({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <section className="ax-rail-card">
      <h3 className="ax-rail-card__title">{title}</h3>
      {children}
    </section>
  );
}
export function RailKv({ k, v }: { k: string; v: ReactNode }): JSX.Element {
  return (
    <div className="ax-kv">
      <span className="ax-kv__k">{k}</span>
      <span className="ax-kv__v">{v}</span>
    </div>
  );
}
export function RailProgress({ percent }: { percent: number | null }): JSX.Element {
  const has = percent !== null && Number.isFinite(percent);
  const clamped = has ? Math.max(0, Math.min(100, percent as number)) : 0;
  return (
    <div className="ax-progress" role="img" aria-label={has ? `${clamped}%` : "No data"}>
      <div className="ax-progress__fill" style={{ width: `${clamped}%` }} />
    </div>
  );
}

// ── Honest empty state ────────────────────────────────────────────────────
export function EmptyInline({ title, children }: { title: string; children?: ReactNode }): JSX.Element {
  return (
    <div className="ax-empty">
      <strong>{title}</strong>
      {children ? <span>{children}</span> : null}
    </div>
  );
}
